import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Features.css';

const Features = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards(prev => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.feature-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: '🏥',
      title: 'Patient Management',
      description: (
        <>
          Our patient registration system provides comprehensive digital records for both 
          {' '}<Link to="/services/opd-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>OPD</Link>{' '} 
          and {' '}<Link to="/services/ipd-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>IPD</Link>{' '} 
          patient workflows with advanced history tracking.
        </>
      ),
      highlights: ['Digital Records', 'Appointment System', 'Medical History']
    },
    {
      icon: '📊',
      title: 'Analytics & Reports',
      description: 'Our hospital ERP software provides real-time insights, performance metrics, and detailed reports to optimize hospital operations.',
      highlights: ['Real-time Data', 'Custom Reports', 'Performance Metrics']
    },
    {
      icon: '💊',
      title: 'Inventory Management',
      description: (
        <>
          Our hospital inventory management software helps you track medications, medical supplies, and equipment with 
          integrated {' '}<Link to="/services/pharmacy-management-software" style={{color: 'var(--primary-blue)', fontWeight: 600}}>Pharmacy module</Link>{' '}
          expiry alerts.
        </>
      ),
      highlights: ['Supply Tracking', 'Auto Reorder', 'Expiry Alerts']
    },
    {
      icon: '👨‍⚕️',
      title: 'Staff Management',
      description: 'Manage doctor schedules, staff assignments, and workforce planning efficiently.',
      highlights: ['Staff Scheduling', 'Role Management', 'Attendance Tracking']
    },
    {
      icon: '💳',
      title: 'Billing & Finance',
      description: (
        <>
          Automated billing, insurance claims, and comprehensive financial management system with complete
          {' '}<Link to="/pricing" style={{color: 'var(--primary-blue)', fontWeight: 600}}>transparent package pricing</Link>.
        </>
      ),
      highlights: ['Auto Billing', 'Insurance Claims', 'Financial Reports']
    },
    {
      icon: '🔐',
      title: 'Security & Compliance',
      description: 'HIPAA compliant with advanced security features and data encryption.',
      highlights: ['HIPAA Compliant', 'Data Encryption', 'Access Control']
    }
  ];

  return (
    <section id="features" className="features" ref={sectionRef}>
      <Helmet>
        <title>Features — Mediway Nextgen HMS</title>
        <meta name="description" content="Features of Mediway Nextgen hospital management software: patient management, billing, analytics, inventory, and security for Indian hospitals." />
        <link rel="canonical" href="https://mediwaynextgen.wayug.com/#features" />
      </Helmet>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">
            Hospital Management Software India Features
          </h2>
          <p className="section-subtitle">
            Everything you need in the best HMS India 2025 standard. From clinical EMR workflows and patient management to robust NABH software India compliant billing.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              data-index={index}
              className={`feature-card ${visibleCards.includes(String(index)) ? 'feature-card--visible' : ''}`}
            >
              <div className="feature-card__content">
                <div className="feature-card__icon">
                  <span className="icon">{feature.icon}</span>
                </div>

                <div className="feature-card__info">
                  <h3 className="feature-card__title">{feature.title}</h3>
                  <p className="feature-card__description">{feature.description}</p>

                  <div className="feature-card__highlights">
                    {feature.highlights.map((highlight, idx) => (
                      <span key={idx} className="highlight-badge">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="feature-card__arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>

              <div className="feature-card__background">
                <div className="background-pattern"></div>
              </div>
            </div>
          ))}
        </div>

        <div className="features-bottom">
          <div className="features-integration">
            <h3>Seamless System &amp; ABHA Integration</h3>
            <p>Mediway Nextgen operates as the ultimate HMS software with ABHA integration, syncing with HL7, FHIR, DICOM, and NHA sandbox registries seamlessly.</p>

            <div className="integration-logos">
              <div className="integration-logo">
                <div className="logo-placeholder">HL7</div>
              </div>
              <div className="integration-logo">
                <div className="logo-placeholder">FHIR</div>
              </div>
              <div className="integration-logo">
                <div className="logo-placeholder">DICOM</div>
              </div>
              <div className="integration-logo">
                <div className="logo-placeholder">NHA ABDM</div>
              </div>
              <div className="integration-logo">
                <div className="logo-placeholder">ISO 27001</div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Features FAQ Section */}
        <div className="features-faq">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Quick answers to common queries about our features and integrations.</p>
          </div>
          <div className="faq-list">
            {[
              {
                q: 'Is Mediway Nextgen HMS compliant with NABH standards?',
                a: 'Yes, absolutely. Our hospital management system is engineered to support NABH accreditation guidelines, quality tracking metrics, and end-to-end compliance workflows for modern Indian hospitals.'
              },
              {
                q: 'Does your platform support CGHS, DGHS, and TPA billing?',
                a: 'Yes. The billing engine supports complex corporate rate-list configurations including CGHS/DGHS mapping, cashless TPA claims pre-authorization tracking, and robust insurance workflows.'
              },
              {
                q: 'How secure is my patient EMR data?',
                a: 'Patient privacy is paramount. We deploy 256-bit SSL encryption, secure AWS cloud server architectures, strict HIPAA compliance, and granular role-based access control (RBAC) safeguards.'
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${visibleCards.includes(String(index)) ? 'faq-item--active' : ''}`}
              >
                <h3 className="faq-question-text">{faq.q}</h3>
                <p className="faq-answer-text">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="features-background">
        <div className="background-gradient"></div>
        <div className="background-shapes">
          <div className="shape shape--1"></div>
          <div className="shape shape--2"></div>
          <div className="shape shape--3"></div>
        </div>
      </div>
    </section>
  );
};

export default Features;